import { In } from 'typeorm'
import { Domain, getRepository } from '@things-factory/shell'
import { KpiMetric } from '../service/kpi-metric/kpi-metric'
import { KpiMetricValue } from '../service/kpi-metric-value/kpi-metric-value'
import { ValueProvider } from '../calculator/provider'
import { getDomainIdsWithAncestors } from '../service/utils/domain-inheritance'

export class KpiMetricValueProvider implements ValueProvider {
  constructor(
    private options: {
      valueDate: string
      org?: string
      domainId: string
      tx?: any
    }
  ) {}

  async get(name: string) {
    const metricRepo = getRepository(KpiMetric, this.options.tx)

    // KpiMetric '정의' 는 부모(admin) 도메인에 있을 수 있어 조상 도메인까지 포함해 조회한다.
    // 단, KpiMetricValue '값' 은 테넌트(현재 도메인)에만 저장되므로 값 조회는 반드시
    // 현재 도메인(this.options.domainId)으로만 한정한다 — 조상을 포함하면 부모/템플릿·타 테넌트
    // 값을 집어 도메인 격리가 깨질 수 있다.
    const domain = await getRepository(Domain, this.options.tx).findOne({ where: { id: this.options.domainId } })
    const domainIds = domain ? await getDomainIdsWithAncestors(domain) : [this.options.domainId]

    const metric = await metricRepo.findOne({ where: { name, domain: { id: In(domainIds) } } })
    if (!metric) throw new Error(`Metric not found: ${name}`)

    // ALLTIME 타입인 경우 valueDate 무시하고 최신 값 조회
    if (metric.periodType === 'ALLTIME') {
      const valueRepo = getRepository(KpiMetricValue, this.options.tx)
      const value = await valueRepo.findOne({
        where: {
          metric: { id: metric.id },
          org: this.options.org ?? '',
          domain: { id: this.options.domainId }
        },
        order: { createdAt: 'DESC' }
      })
      if (!value) {
        throw new Error(`Metric value not found: metric='${name}', org='${this.options.org ?? ''}' (ALLTIME type)`)
      }
      return value.value
    }

    const valueRepo = getRepository(KpiMetricValue, this.options.tx)

    // valueDate 미지정 (예: KPI 재계산) → 그 metric 의 '최신 월'(가장 최근 valueDate) 값을
    // 대표값으로 사용한다. 완료평가/'월별 데이터' 탭에서 최신월을 수정하면 이 값이 곧 KPI
    // 계산 입력이 된다.
    // (MONTH 는 valueDate 를 'YYYY-MM-01' 로 저장하는데, 과거엔 current-month('YYYY-MM')
    //  exact 조회라 포맷 불일치로 못 찾고 우연히 아래 fallback 으로만 동작했음 — 명시적 최신월 조회)
    if (!this.options.valueDate) {
      const latest = await valueRepo.findOne({
        where: {
          metric: { id: metric.id },
          org: this.options.org ?? '',
          domain: { id: this.options.domainId },
          // metric 의 periodType 과 같은 값만 — MONTH 지표인데 섞여 있는 DAY 행이 valueDate 로
          // 더 최신이라 잘못 집히던 문제 방지 (예: 실적공정률 MONTH 2026-05 vs 잔여 DAY 2026-06-15).
          periodType: metric.periodType
        },
        order: { valueDate: 'DESC' }
      })
      if (!latest) {
        throw new Error(`Metric value not found: metric='${name}', org='${this.options.org ?? ''}' (latest)`)
      }
      return latest.value
    }

    // 명시적 valueDate 가 주어진 경우: 해당 기간 조회 (없으면 최신 fallback).
    let valueDate = this.options.valueDate
    // valueDate가 metric.periodType에 맞는 포맷인지 검사, 아니면 보정
    if (metric.periodType === 'MONTH' && valueDate.length > 7) valueDate = valueDate.slice(0, 7)
    if (metric.periodType === 'DAY' && valueDate.length > 10) valueDate = valueDate.slice(0, 10)
    var value = await valueRepo.findOne({
      where: {
        metric: { id: metric.id },
        valueDate,
        org: this.options.org ?? '',
        domain: { id: this.options.domainId },
        periodType: metric.periodType
      }
    })
    if (!value) {
      // 해당 기간 데이터 없으면 최신 데이터로 fallback.
      value = await valueRepo.findOne({
        where: {
          metric: { id: metric.id },
          org: this.options.org ?? '',
          domain: { id: this.options.domainId },
          periodType: metric.periodType
        },
        order: { valueDate: 'DESC' }
      })

      if (!value) {
        throw new Error(
          `Metric value not found: metric='${name}', org='${this.options.org ?? ''}', valueDate='${valueDate}', periodType='${metric.periodType}'`
        )
      }
    }

    return value.value
  }
}
